import React, { HTMLProps } from 'react'
import { SystemProps } from '../theme/system'
import { TextDecorationProps } from '../theme/typography'
import { Text } from '../text'
import { Box } from '../box'
import { Props as IconProps } from '../icon'
import { Loader } from '../loader'
import { ButtonStyled } from './ButtonStyled'

interface BaseButtonProps {
  block?: boolean
  children?: React.ReactNode
  className?: string
  danger?: boolean
  'data-test-id'?: string
  endIcon?: React.ReactElement<IconProps>
  ghost?: boolean
  loading?: boolean | { delay?: number }
  prefixCls?: string
  startIcon?: React.ReactElement<IconProps>
}

type ButtonHTMLType = 'submit' | 'button' | 'reset'

type NativeButtonProps = {
  htmlType?: ButtonHTMLType
  onClick?: React.MouseEventHandler<HTMLElement>
} & BaseButtonProps &
  Omit<React.ButtonHTMLAttributes<any>, 'type' | 'onClick'>

export type Props = SystemProps &
  NativeButtonProps &
  TextDecorationProps &
  HTMLProps<HTMLAnchorElement | HTMLButtonElement>

export const Button = ({
  color,
  children,
  htmlType,
  startIcon,
  endIcon,
  loading,
  disabled,
  onClick,
  ...other
}: Props) => {
  const textOffset = children ? 2 : 0

  return (
    <ButtonStyled
      display='flex'
      alignItems='center'
      justifyContent='center'
      textDecoration='none'
      m={0}
      py={0}
      px={4}
      h={8}
      borderRadius={8}
      boxShadow='inline-xs-neutral-dark-12'
      bg='white'
      onClick={onClick}
      disabled={disabled || loading}
      type={htmlType}
      {...other}
    >
      {startIcon && !loading && <Box mr={textOffset}>{startIcon}</Box>}
      {loading && (
        <Box mr={textOffset}>
          <Loader size={4} color={color} />
        </Box>
      )}
      {children && (
        <Text
          fontWeight={600}
          color={disabled ? 'neutral-dark-24' : color}
          fontSize={other.fontSize ?? 14}
        >
          {children}
        </Text>
      )}
      {endIcon && <Box ml={2}>{endIcon}</Box>}
    </ButtonStyled>
  )
}
